import React, { useState, useEffect } from 'react';
import { useFirestore, useFirestoreDocDataOnce } from 'reactfire';
import { doc } from 'firebase/firestore';
import {
  Box,
  Typography,
  Alert,
  Button,
  CircularProgress,
  Container,
  Paper,
} from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import useGlobal from 'global-state/store';
import PdfScannerUpload from './PdfScannerUpload';

export default function ScanCertificatePdf() {
  const [globalState] = useGlobal();
  const navigate = useNavigate();
  const location = useLocation();
  const firestore = useFirestore();

  const orgAppRef = globalState.activeOrganization
    ? doc(firestore, `organizations/${globalState.activeOrganization}/apps/digitank-gauger`)
    : null;

  const { data: orgAppData, status } = useFirestoreDocDataOnce(orgAppRef);
  const featureEnabled = orgAppData?.documentAiEnabled === true;

  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState(null);
  const [certificateType, setCertificateType] = useState('gauged_container');

  useEffect(() => {
    if (location.state?.certificateType) {
      setCertificateType(location.state.certificateType);
    }
  }, [location.state]);

  const handlePdfProcessed = (data) => {
    setProcessing(false);
    setError(null);

    if (data?.scanId) {
      navigate(`/certificates/scanned/${data.scanId}`, { replace: true });
      return;
    }

    // Unexpected response: surface an error instead of leaving the user stuck
    setError('Analyse terminée mais aucune redirection possible. Veuillez réessayer.');
    // eslint-disable-next-line no-console
    console.error('PdfScannerUpload returned without scanId:', data);
  };

  const handleProcessingError = (errorMessage) => {
    setError(errorMessage);
    setProcessing(false);
  };

  const handleStartProcessing = () => {
    setProcessing(true);
    setError(null);
  };

  // Loading state
  if (status === 'loading') {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '50vh',
          }}
        >
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  // Feature not enabled
  if (!featureEnabled) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h4" component="h1" gutterBottom>
            Scanner de PDF
          </Typography>

          <Alert severity="info" sx={{ mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Fonctionnalité non activée
            </Typography>
            <Typography>
              Contactez les ventes pour activer cette fonctionnalité.
            </Typography>
          </Alert>

          <Box sx={{ mt: 3 }}>
            <Button
              variant="contained"
              onClick={() => navigate('/certificates/create')}
              sx={{ mr: 2 }}
            >
              Ajouter un Certificat
            </Button>
            <Button
              variant="outlined"
              onClick={() => navigate('/contact')}
            >
              Contact
            </Button>
          </Box>
        </Paper>
      </Container>
    );
  }

  // Main scanning interface
  return (
    <Box sx={{
      width: '100vw',
      marginLeft: '-50vw',
      left: '50%',
      position: 'relative',
      height: '100vh',
    }}
    >
      <Box sx={{ px: 2, height: '100%' }}>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {/* Upload State */}
        {!processing && (
          <Box sx={{ height: 'calc(100% - 40px)' }}>
            <PdfScannerUpload
              onPdfProcessed={handlePdfProcessed}
              onProcessingError={handleProcessingError}
              onStartProcessing={handleStartProcessing}
              organizationId={globalState.activeOrganization}
              certificateType={certificateType}
              onCertificateTypeChange={setCertificateType}
            />
          </Box>
        )}

        {/* Processing State */}
        {processing && (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              height: 'calc(100% - 40px)',
            }}
          >
            <CircularProgress size={60} sx={{ mb: 2 }} />
            <Typography variant="h6" gutterBottom>
              Traitement en cours...
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Analyse du PDF et extraction des données...
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
}
